import { AlertCircle, CircleStop, Globe, RotateCcw, WifiOff } from "lucide-react";
import { LogoMark } from "@/components/brand/Logo";
import { CopyButton } from "@/components/chat/CopyButton";
import { Markdown } from "@/components/chat/Markdown";
import { AttachmentChips } from "@/components/files/AttachmentChips";
import { cn } from "@/lib/utils";

const STATUS_NOTES = {
  stopped: { icon: CircleStop, text: "You stopped this response." },
  interrupted: { icon: AlertCircle, text: "This response was interrupted." },
  failed: { icon: AlertCircle, text: "ChatSleek couldn't generate a response. Please retry." },
  disconnected: { icon: WifiOff, text: "Connection lost. The answer may still be finishing — reloading shortly." },
};

function Thinking() {
  return (
    <div className="flex h-7 items-center gap-1.5 cs-thinking" data-testid="assistant-thinking-indicator" aria-label="ChatSleek is thinking">
      <span />
      <span />
      <span />
    </div>
  );
}

export function AssistantMessage({ message, isLast, onRetry, canRetry }) {
  const streaming = message.status === "streaming";
  const note = STATUS_NOTES[message.status];
  const NoteIcon = note?.icon;
  const showActions = !streaming && message.status !== "disconnected";
  return (
    <div className="group flex gap-3.5 sm:gap-4 cs-rise" data-testid="chat-message-assistant" data-status={message.status}>
      <div className="pt-0.5">
        <LogoMark size={26} />
      </div>
      <div className="min-w-0 flex-1">
        {streaming && !message.content ? (
          <Thinking />
        ) : (
          <div className={cn("cs-prose", streaming && "cs-caret")} data-testid="assistant-message-content">
            {message.content ? <Markdown content={message.content} /> : null}
          </div>
        )}
        {note && (
          <div
            className={cn("mt-2 flex items-center gap-2 text-[12.5px]", message.status === "failed" ? "text-rose-300/90" : "text-zinc-500")}
            data-testid={`assistant-status-${message.status}`}
          >
            <NoteIcon className="h-3.5 w-3.5" strokeWidth={1.8} />
            {note.text}
          </div>
        )}
        {showActions && (
          <div
            className={cn(
              "mt-2 -ml-2 flex items-center gap-0.5 transition-opacity duration-150",
              isLast ? "opacity-100" : "opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100",
            )}
            data-testid="assistant-message-actions"
          >
            {message.content && <CopyButton text={message.content} testId="assistant-copy-button" />}
            {isLast && canRetry && (
              <button
                type="button"
                onClick={onRetry}
                data-testid="assistant-retry-button"
                className="inline-flex h-7 items-center gap-1.5 rounded-md px-2 text-[12px] text-zinc-500 transition-colors hover:bg-white/[0.06] hover:text-zinc-100"
              >
                <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.8} />
                Retry
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function UserMessage({ message, attachments, badge }) {
  const unsent = message.status === "unsent";
  const files = attachments || message.attachments;
  return (
    <div className="flex flex-col items-end gap-2 cs-rise" data-testid="chat-message-user">
      {badge && <span className="inline-flex items-center gap-1.5 text-[11.5px] text-zinc-500" data-testid="chat-message-research-badge"><Globe className="h-3 w-3" />{badge}</span>}
      {files?.length > 0 && <AttachmentChips items={files.map((f) => ({ ...f, status: "completed", ai_readable: true }))} className="max-w-[85%] justify-end" />}
      <div
        className={cn(
          "cs-user-text max-w-[85%] whitespace-pre-wrap break-words rounded-[20px] rounded-br-md bg-ink-750 px-4 py-2.5 text-[15px] leading-relaxed text-zinc-100 sm:max-w-[78%]",
          unsent && "opacity-60",
        )}
      >
        {message.content}
      </div>
    </div>
  );
}
